import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { DocumentObjectService } from '@Core/models/proprietary/document/document-object.service';
import { MultiDocumentObjectService } from '@Core/models/proprietary/document/multidocument-object.service';
import { MultiDocumentObjectState } from '@Core/models/proprietary/document/multidocument-object.state';
import { TaskObjectService } from '@Core/models/proprietary/task/task-object.service';
import { TaskObjectState } from '@Core/models/proprietary/task/task-object.state';
import { EventType as TaskEventType, TaskActionType } from '@Core/models/proprietary/task/task-object.types';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataSourceStatus } from '@Core/services/proprietary/datasource';
import { FileActionType } from '@Core/services/proprietary/document-file.types';
import { ObjectService } from '@Core/services/proprietary/object.service';
import { cloneDeep } from 'src/ep-utils';
import { TaskActionComponent } from '../../task-controls/task-action/task-action.component';

@Component({
    standalone: false,
    selector: 'ep-multi-document-file',
    templateUrl: './multi-document-file.component.html'
})
export class MultiDocumentFileComponent implements OnInit, OnDestroy {
    @Select(MultiDocumentObjectState.documents)
    documents$: Observable<any>;
    documentsSub: Subscription;
    documents: any[];

    @Select(MultiDocumentObjectState.filings)
    filings$: Observable<any>;

    @Select(TaskObjectState.task)
    task$: Observable<any>;
    taskSub: Subscription;
    task: any;

    dsTaskProcessSub: Subscription;
    dsTaskGetSub: Subscription;
    dsDocumentProcessSub: Subscription;

    model = {
        inProgress: false,
        filingComplete: false,
        fileNumber: -1,
        action: FileActionType.SaveOnly,
        taskLinkType: 'NOTASK',
        taskObjectCode: 'Task',
        autoOpenTask: false
    };

    constructor(
        public documentOS: DocumentObjectService,
        public multiDocOS: MultiDocumentObjectService,
        public taskOS: TaskObjectService,
        private store: Store,
        public activeModal: NgbActiveModal,
        public app: AppService,
        public os: ObjectService,
        private modalService: NgbModal
    ) { }

    ngOnInit() {
        this.documentsSub = this.documents$.subscribe((docs) => {
            if (docs) {
                this.documents = docs;
            }
        });
        this.taskSub = this.task$.subscribe((task) => {
            if (task) {
                this.task = task;
            }
        });
        this.taskOS.newTask({
            Task_Desc: 'Multi Doc File Manual Task',
            Task_Object: { Object_Code: 'Task' }
        });
    }

    ngOnDestroy() {
        if (this.taskSub) {
            this.taskSub.unsubscribe();
        }
        if (this.documentsSub) {
            this.documentsSub.unsubscribe();
        }
        if (this.dsTaskProcessSub) {
            this.dsTaskProcessSub.unsubscribe();
        }
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
        if (this.dsDocumentProcessSub) {
            this.dsDocumentProcessSub.unsubscribe();
        }
    }

    save() {
        this.model.action = FileActionType.SaveOnly;
        this.process();
    }

    saveAndStartTask() {
        this.model.action = FileActionType.StartTask;
        this.model.autoOpenTask = true;
        this.process();
    }

    saveAndDistribute() {
        this.model.action = FileActionType.Distribute;
        this.process();
    }

    saveAndRoute() {
        this.model.action = FileActionType.Route;
        this.process();
    }

    async process() {
        let docIDs: string[] = [];
        let docs = cloneDeep(this.documents);

        // Set Filing in Progress flags
        this.model.inProgress = true;
        this.model.fileNumber = 0;

        for (var i in docs) {
            docIDs.push(docs[i].Doc_ID);
            this.model.fileNumber++;
            if ((await this.multiDocOS.processDocument(docs[i])) === true) {
                continue;
            }
        }

        // Filing Complete
        this.model.filingComplete = true;
        this.model.inProgress = false;
        await this.setTimer(2);
        this.model.filingComplete = false;

        this.postProcess(docIDs.toString());
    }

    postProcess(docIDs: string) {
        // Reset Filing in Progress flags
        this.model.inProgress = false;
        this.model.fileNumber = -1;

        if (this.model.taskLinkType != 'NOTASK') {
            // For future updates to link filings to tasks
            this.app.dsTaskProcess.reset();
            this.app.dsTaskGet.reset();

            this.taskOS.process(TaskEventType.Update, {
                Auto_File_Doc_ID: docIDs
            });

            this.dsTaskProcessSub = this.app.dsTaskProcess.status$.subscribe((status) => {
                this.taskProcessStatusUpdated(status);
            });
            this.dsTaskGetSub = this.app.dsTaskGet.status$.subscribe((status) => {
                this.taskGetStatusUpdated(status);
            });
        } else {
            this.taskOS.notifyDocumentSaved();

            if (this.model.action == FileActionType.Route) {
                this.taskOS.newTask({
                    Task_Desc: 'Route Documents', //: ' + this.document.Doc_Name,
                    Task_Object: {
                        Object_ID: docIDs, //this.document.Doc_ID,
                        Object_Code: 'Document'
                    },
                    Task_Required: 'N'
                });

                const modalRef = this.modalService.open(TaskActionComponent, { centered: true, backdrop: 'static' });
                modalRef.componentInstance.taskActionMode = TaskActionType.Route;
            }

            this.closeModal();
        }
    }

    cancel() {
        this.model.action = FileActionType.Cancel;
        this.closeModal();
    }

    //fired after update complete
    taskProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Error:
                if (this.dsTaskProcessSub) {
                    this.dsTaskProcessSub.unsubscribe();
                }
                if (this.dsTaskGetSub) {
                    this.dsTaskGetSub.unsubscribe(); //clear "get" subscription since it will never be called on error
                }
                break;
        }
    }

    //fired after update reloads the new task
    taskGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsTaskProcessSub) {
                    this.dsTaskProcessSub.unsubscribe();
                }
                if (this.dsTaskGetSub) {
                    this.dsTaskGetSub.unsubscribe();
                }
                let task = this.app.dsTaskGet.objectNode; //get the task from the reply -- task created

                switch (this.model.action) {
                    case FileActionType.Distribute:
                        break;
                    case FileActionType.StartTask:
                        this.taskOS.openObjectFromTask(task);
                        break;
                    case FileActionType.SaveOnly:
                        // if not in a session, then load the new task into task header
                        if (!this.taskOS.isProjectActive) {
                            let taskID = task.Task_ID;
                            if (taskID) {
                                this.taskOS.loadTask(taskID, false);
                            }
                        }
                        this.taskOS.suppressTaskHeader(false);
                        break;
                    case FileActionType.Route:
                        this.taskOS.loadTask(task.Task_ID, false);

                        const modalRef = this.modalService.open(TaskActionComponent, { centered: true, backdrop: 'static' });
                        modalRef.componentInstance.taskActionMode = TaskActionType.Route;
                        break;

                    default:
                        break;
                }

                this.taskOS.notifyDocumentSaved();
                this.closeModal();
                break;
            case DataSourceStatus.Error:
                if (this.dsTaskProcessSub) {
                    this.dsTaskProcessSub.unsubscribe();
                }
                if (this.dsTaskGetSub) {
                    this.dsTaskGetSub.unsubscribe();
                }
                break;
        }
    }

    closeModal() {
        this.activeModal.close({
            action: this.model.action,
            documents: this.documents
        });
        this.multiDocOS.reset();
    }

    setTimer(seconds: number) {
        return new Promise(async (resolve) => {
            let intervalId = setInterval(() => {
                seconds--;
                if (seconds === 0) {
                    clearInterval(intervalId);
                    resolve(true);
                }
            }, 1000);
        });
    }
}
